import React, { useCallback, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import classnames from 'classnames';
import { theme, Theme } from '@app/theme';
import { Account, Participant } from '@app/api';
import { InformationHeader } from '@app/core';
import tandemz from '@app/illu/Branding/Tandemz.svg';
import { useTranslation } from 'react-i18next';
import { ProfileMenu } from './ProfileMenu';
import useMediaQuery from '@mui/material/useMediaQuery';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'sticky',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: [theme.marginBase * 3, theme.marginBase * 5],
    backgroundColor: theme.colors.white,
    top: 0,
    zIndex: 2,
    boxShadow: theme.boxShadow.base,
    [theme.media.mobile]: {
      padding: theme.marginBase * 3,
    },
  },
  headerContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.marginBase * 2,
    [theme.media.mobile]: {
      gap: theme.marginBase / 2,
    },
  },
  childrenContainer: {
    width: '100%',
    marginTop: theme.marginBase * 2,
    transition:
      'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), margin 0.25s cubic-bezier(0.4, 0, 0.2, 1), padding 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
  },
  childrenContainerShow: {
    maxHeight: '500px',
    opacity: 1,
  },
  childrenContainerHide: {
    opacity: 0,
    maxHeight: 0,
    marginTop: 0,
    padding: 0,
    pointerEvents: 'none',
  },
  profileHeader: {
    gap: theme.marginBase,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    margin: 0,
    padding: 0,
    width: 'fit-content',
  },
  separator: {
    flex: 1,
  },
  titleContainer: {
    width: 'fit-content !important',
    flexDirection: 'row-reverse',
    gap: theme.marginBase * 3,
    marginBottom: '0px !important',
    [theme.media.mobile]: {
      gap: theme.marginBase,
    },
  },
  name: {
    ...theme.fonts.label,
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  title: {
    ...theme.fonts.h4,
    [theme.media.mobile]: {
      display: 'inline-block',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '50ch',
    },
  },
  notification: {
    position: 'relative',
  },
  isMobileMessageOpened: {
    boxShadow: 'none',
  },
}));

interface Props {
  user: Participant | Account | undefined;
  tagsChildren?: React.ReactNode;
  bottomChildren?: React.ReactNode;
  title?: string;
  openModalMessages?: () => any;
  messagesOpened?: boolean;
  participationId?: string;
  noLogin?: boolean;
  currentScrollY?: number;
}

export const HeaderConnect = ({
  user,
  title,
  tagsChildren,
  bottomChildren,
  openModalMessages,
  messagesOpened,
  participationId,
  noLogin,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery(theme.media.mobile);

  const [displayChildren, setDisplayChildren] = useState(true);
  const hideScrollY = React.useRef(0);
  const lastScrollY = React.useRef(0);

  useEffect(() => {
    if (!isMobile) {
      setDisplayChildren(true);
      return;
    }

    if (messagesOpened) {
      setDisplayChildren(false);
      return;
    }

    const handleScroll = (event: Event) => {
      if (messagesOpened) {
        setDisplayChildren(false);
        return;
      }

      const target = event.target;
      const minOffsetPx = 50;
      const deltaDownPx = 5;
      const minUpwardDistance = 70;

      let currentScrollY = 0;
      if (
        target === document ||
        target === document.documentElement ||
        target === window
      ) {
        currentScrollY = window.scrollY || document.documentElement.scrollTop;
      } else if (target instanceof HTMLElement) {
        currentScrollY = target.scrollTop;
      }

      const preventBounceY = Math.max(0, currentScrollY);
      const difference = preventBounceY - lastScrollY.current;

      if (difference === 0) {
        return;
      }

      if (difference > deltaDownPx && preventBounceY > minOffsetPx) {
        setDisplayChildren(false);
        hideScrollY.current = preventBounceY;
        lastScrollY.current = preventBounceY;
      } else if (difference < 0) {
        const upwardDistance = hideScrollY.current - preventBounceY;
        if (upwardDistance >= minUpwardDistance) {
          setDisplayChildren(true);
        }
        lastScrollY.current = preventBounceY;
      } else {
        lastScrollY.current = preventBounceY;
      }
    };

    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMobile, messagesOpened]);

  const _onOpenModalMessages = openModalMessages
    ? () => {
        openModalMessages();
        if (isMobile && messagesOpened) {
          setDisplayChildren(false);
        }
      }
    : undefined;

  return (
    <div
      className={classnames(classes.container, {
        [classes.isMobileMessageOpened]: isMobile && messagesOpened,
      })}
    >
      <div className={classes.headerContainer}>
        {user && <InformationHeader className={classes.titleContainer} />}
        {(!user || noLogin) && <img src={tandemz} alt="tandemz" />}
        {title && !!user && !tagsChildren && (
          <h1 className={classes.title}>{t(title)}</h1>
        )}
        {tagsChildren}
        <div className={classes.separator} />
        {!noLogin && (
          <ProfileMenu
            user={user}
            openModalMessages={_onOpenModalMessages}
            participationId={participationId}
          />
        )}
      </div>
      {(!!bottomChildren || (tagsChildren && title)) && (
        <div
          className={classnames(classes.childrenContainer, {
            [classes.childrenContainerShow]: displayChildren,
            [classes.childrenContainerHide]: !displayChildren && isMobile,
          })}
        >
          {bottomChildren}
          {tagsChildren && title && <h1 className={classes.title}>{title}</h1>}
        </div>
      )}
    </div>
  );
};
